<!--
  App-wide, while signed in: starts the tab's local-agent state and serving
  (state/relayAgents.svelte.ts), and renders the dock, one fixed column at
  the top right under the top bar and any new-build banner, clear of the
  toast and the range bar at the bottom. In it, from the store:
  - the approval cards of the views on screen (RelayApproval registers
    them: Settings every one), each arming its Connect only once it has
    stayed put (RelayApprovalCard); a PR page's own go to its header's
    agent panel instead (RelayAgentsButton, relayAgents.host), as do its
    notices;
  - for the other approvals, a line linking to their subject's page (a
    pull request's), or to Settings for a subject with no page here;
  - for each request for a history that resolves to nothing (`unfound`),
    a line saying why, with Decline, on every page;
  - the notice of what came of the last one, when its view is on screen;
  - while the tab's encrypted agent connection is down (it kept stopping,
    relay.workerDown), a line saying so on every page, with Try again.
  Later folds the cards into one line with their count; opening it shows
  them armed anew. A status notice clears by itself after a while, unless
  focus or the pointer is on it or it is a warning to act on (`persist`).
  Everything new is said in two live regions kept mounted, in turn.
-->
<script lang="ts">
  import { onMount, tick } from 'svelte';
  import { kindName, subjectHref } from '../logic/relayText';
  import { relayAgents, type Notice } from '../state/relayAgents.svelte';
  import RelayApprovalCard from './RelayApprovalCard.svelte';
  onMount(() => relayAgents.start());

  // A pull request's own approvals and notices go to its header's agent panel (relayAgents.host), not here.
  const inline = $derived(relayAgents.approvals.filter((a) => relayAgents.shownInline(a.subject) && !relayAgents.hosted(a.subject)));
  const elsewhere = $derived(relayAgents.approvals.filter((a) => !relayAgents.shownInline(a.subject)));
  const order = $derived(inline.map((a) => a.key).join('\n'));
  const linked = $derived(relayAgents.views.includes('all'));
  const notice = $derived.by(() => {
    const n = relayAgents.notice;
    return n && (!n.subject || (relayAgents.shownInline(n.subject) && !relayAgents.hosted(n.subject))) ? n : null;
  });
  const count = (n: number) => (n === 1 ? '1 agent connection' : `${n} agent connections`);

  // Said in turn in two live regions, so the same words are said again, and
  // what arrives before a write lands is said with it.
  let live = $state(['', '']);
  let turn = 0;
  let queued: string[] = [];
  function say(text: string) {
    queued.push(text);
    if (queued.length > 1) return;
    void tick().then(() => {
      const all = queued.join(' ');
      queued = [];
      turn = 1 - turn;
      live = turn ? ['', all] : [all, ''];
    });
  }
  let heard = new Set<string>();
  $effect(() => {
    const now = relayAgents.approvals;
    const fresh = now.filter((a) => !heard.has(a.key));
    heard = new Set(now.map((a) => a.key));
    if (fresh.length) say(fresh.map((a) => `${a.name ?? 'An agent'} asks to read ${a.subject}.`).join(' '));
  });
  let told: Notice | null = null;
  // A notice is said when it is shown (its view on screen).
  $effect(() => {
    const n = notice;
    if (n && n !== told) say(n.text);
    told = n;
  });

  let held = $state(false);
  // A new notice, or none: what held the last one (hover, focus) holds it no more.
  $effect(() => { void notice; held = false; });
  $effect(() => {
    if (!notice || held) return;
    // Held also by a pointer resting on it, or focus in it, since a notice replaced it (no enter event then).
    return relayAgents.expire(notice, () => held || !!line?.matches(':hover, :focus-within'));
  });

  let later = $state(false);
  $effect(() => { if (!inline.length) later = false; });
  let dock: HTMLElement | undefined = $state();
  let line: HTMLElement | undefined = $state();
  let pill: HTMLButtonElement | undefined = $state();
  const focusLost = () => !document.activeElement || document.activeElement === document.body;
  /** Focus for when what held it went away: the notice, the next card, the pill, else the page's heading. */
  async function refocus() {
    await tick();
    if (!focusLost()) return;
    const next = line ?? dock?.querySelector<HTMLElement>('.relay-approval h3') ?? pill;
    if (next) return next.focus();
    const page = document.querySelector<HTMLElement>('main h1, h1') ?? document.querySelector<HTMLElement>('main');
    if (!page) return;
    if (!page.hasAttribute('tabindex')) page.setAttribute('tabindex', '-1');
    page.focus();
  }
  async function fold(on: boolean) {
    later = on;
    await tick();
    if (on) pill?.focus();
    else dock?.querySelector<HTMLElement>('.relay-approval h3')?.focus();
  }
  /** Starts the connection again; the line goes once it is up, so focus moves to the notice saying so. */
  async function tryAgain() {
    if (relayAgents.reviving) return;
    await relayAgents.revive();
    void refocus();
  }
  function dismiss() {
    relayAgents.dismissNotice();
    void refocus();
  }
</script>

<div class="sr-only" aria-live="polite">{live[0]}</div>
<div class="sr-only" aria-live="polite">{live[1]}</div>
<div class="relay-dock" bind:this={dock}>
  {#if inline.length}
    <div class="relay-approvals" role="region" aria-label="Agent connections to approve">
      {#if later}
        <button class="btn small pill" bind:this={pill} aria-expanded="false" onclick={() => fold(false)}>{count(inline.length)} to approve</button>
      {:else}
        <div class="head">
          <span class="muted small">{count(inline.length)} to approve</span>
          <button class="btn small" aria-expanded="true" onclick={() => fold(true)}>Later</button>
        </div>
        {#each inline as approval (approval.key)}<RelayApprovalCard {approval} {order} {linked} onleave={refocus} />{/each}
      {/if}
    </div>
  {/if}
  {#if elsewhere.length || relayAgents.unfound.length || notice || relayAgents.workerDown}
    <aside class="relay-notices" aria-label="Local agents">
      {#if relayAgents.workerDown}
        <p class="relay-down alert" role="alert">
          <span>{relayAgents.workerDown}</span>
          <button class="btn small" aria-disabled={relayAgents.reviving ? 'true' : undefined} onclick={tryAgain}>Try again</button>
        </p>
      {/if}
      {#each relayAgents.unfound as u (u.key)}
        <p class="relay-unfound" role="alert">
          <span><b>{u.name}</b> asks to read the history <span class="mono">{u.locator}</span>, which Changeling can’t find: {u.why}</span>
          <button class="btn small" onclick={() => void relayAgents.declineUnfound(u).then(refocus, () => {})}>Decline</button>
        </p>
      {/each}
      {#each elsewhere as a (a.key)}
        {@const href = subjectHref(a.subject)}
        <p>
          <span><b>{a.name ?? 'An agent'}</b> (<span class="mono">{a.fingerprint}</span>) asks to read {a.subject}.</span>
          {#if href}<a class="btn small" {href}>Review on the {kindName(a.subject)}</a>{:else}<a class="btn small" href="/settings">Review in Settings</a>{/if}
        </p>
      {/each}
      {#if notice}
        <!-- svelte-ignore a11y_no_static_element_interactions (hover only keeps the notice up) -->
        <p
          class="relay-notice"
          class:alert={notice.tone === 'alert'}
          class:warn={notice.persist}
          tabindex="-1"
          bind:this={line}
          onmouseenter={() => (held = true)}
          onmouseleave={() => (held = false)}
          onfocusin={() => (held = true)}
          onfocusout={() => (held = false)}
        >
          <span>{notice.text}</span>
          <button class="btn small" onclick={dismiss}>Dismiss</button>
        </p>
      {/if}
    </aside>
  {/if}
</div>

<style>
  .relay-dock {
    position: fixed;
    top: calc(var(--topbar-h) + var(--new-build-h, 0px) + 8px);
    right: 16px;
    z-index: 56;
    width: min(440px, calc(100vw - 32px));
    max-height: calc(100vh - var(--topbar-h) - var(--new-build-h, 0px) - 16px);
    max-height: calc(100dvh - var(--topbar-h) - var(--new-build-h, 0px) - 16px);
    overflow-y: auto;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
    gap: 8px;
    pointer-events: none;
  }
  .relay-dock > * { pointer-events: auto; }
  .relay-approvals, .relay-notices { display: grid; gap: 8px; }
  .head { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 6px 10px; background: var(--surface); border: 1px solid var(--line); border-radius: 8px; box-shadow: 0 10px 30px #0003; }
  .pill { justify-self: end; border-color: var(--accent); box-shadow: 0 10px 30px #0003; }
  p { display: flex; gap: 10px; align-items: center; justify-content: space-between; margin: 0; padding: 10px 12px; background: var(--surface); border: 1px solid var(--accent); border-radius: 8px; font-size: 13px; box-shadow: 0 10px 30px #0003; }
  p:focus { outline: 2px solid var(--accent); outline-offset: 2px; }
  p.alert { border-color: var(--bad); }
  p.warn { border-color: var(--warn); background: var(--warnbg); }
  p > span { min-width: 0; overflow-wrap: anywhere; }
  .btn { flex: none; }
  .btn[aria-disabled='true'] { opacity: .45; cursor: default; }
</style>
